import React from 'react';
import styles from '../IntradaySignalTestPage.module.css';
import { IntradayTradeRow } from '../../types/trading';

interface TradeTableProps {
  rows: IntradayTradeRow[];
}

interface StrategyTotal {
  strategyNo: number;
  label: string;
  closed: number;
  pnl: number;
}

const formatPrice = (value: number | null | undefined): string =>
  typeof value === 'number' && Number.isFinite(value) ? value.toFixed(4) : '--';

const formatPnl = (value: number | null | undefined): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '--';
  return `${value >= 0 ? '+' : ''}${value.toFixed(4)}`;
};

const pnlClass = (value: number | null | undefined): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '';
  return value >= 0 ? styles.pnlPositive : styles.pnlNegative;
};

const summarize = (rows: IntradayTradeRow[]): StrategyTotal[] => {
  const totals = new Map<number, StrategyTotal>();
  rows.forEach((row) => {
    const entry = totals.get(row.strategy_no) ?? {
      strategyNo: row.strategy_no,
      label: row.strategy_label,
      closed: 0,
      pnl: 0,
    };
    if (typeof row.pnl === 'number' && Number.isFinite(row.pnl)) {
      entry.closed += 1;
      entry.pnl += row.pnl;
    }
    totals.set(row.strategy_no, entry);
  });
  return Array.from(totals.values()).sort((a, b) => a.strategyNo - b.strategyNo);
};

export const TradeTable: React.FC<TradeTableProps> = ({ rows }) => {
  const closed = rows.filter((row) => typeof row.pnl === 'number');
  const totalPnl = closed.length > 0 ? closed[closed.length - 1].cum_pnl : null;
  const perStrategy = summarize(rows);

  return (
    <section className={styles.chartSection}>
      <div className={styles.chartSectionHeader}>
        <h2>Trades</h2>
        <p>
          Every strategy writes here in tick order. Entry is recorded when a strategy fires, exit
          when its opposite condition hits. One unit per trade: a long pays exit minus entry, a
          short pays entry minus exit.
        </p>
      </div>

      <div className={styles.tableScroll}>
        <table className={styles.tradeTable} data-testid="intraday-trade-table">
          <thead>
            <tr>
              <th>Trade No.</th>
              <th>Strategy</th>
              <th>Direction</th>
              <th>Entry</th>
              <th>Exit</th>
              <th>PnL</th>
              <th>Cum PnL</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={7} className={styles.tradeEmpty}>
                  No trades yet
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.trade_no} data-testid={`intraday-trade-row-${row.trade_no}`}>
                  <td>{row.trade_no}</td>
                  <td className={styles.strategyCell}>
                    #{row.strategy_no} {row.strategy_label}
                  </td>
                  <td className={styles.directionCell}>{row.direction === 'short' ? 'Short' : 'Long'}</td>
                  <td>{formatPrice(row.entry_price)}</td>
                  <td>{formatPrice(row.exit_price)}</td>
                  <td className={pnlClass(row.pnl)}>{formatPnl(row.pnl)}</td>
                  <td className={pnlClass(row.cum_pnl)}>{formatPnl(row.cum_pnl)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className={styles.statusRow}>
        <span>Closed Trades: {closed.length}</span>
        <span data-testid="intraday-total-pnl" className={pnlClass(totalPnl)}>
          Total PnL: {formatPnl(totalPnl)}
        </span>
      </div>

      {perStrategy.length > 0 ? (
        <div className={styles.statusRow} data-testid="intraday-strategy-summary">
          {perStrategy.map((total) => (
            <span key={total.strategyNo} className={pnlClass(total.pnl)}>
              #{total.strategyNo} {total.label}: {formatPnl(total.pnl)} ({total.closed})
            </span>
          ))}
        </div>
      ) : null}
    </section>
  );
};
